import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { useDateFormat } from '@/hooks/useDateFormat';
import type { ApprovalTurnaroundRow, PendingApprovalAgingRow } from '@/types/reports';
import { withId } from './reportHelpers';

const TABS = ['pendingApprovalsAging', 'approvalTurnaround'] as const;

export default function ApprovalsReportsPage() {
  const { t } = useTranslation('reports');
  const { formatDate } = useDateFormat();
  const [tab, setTab] = useState(0);

  const { data: pending = [], isLoading: loadingPending } = useQuery({
    queryKey: ['reports', 'pending-approvals-aging'],
    queryFn: async () => (await apiClient.get<PendingApprovalAgingRow[]>('/reports/pending-approvals-aging')).data,
    enabled: tab === 0,
  });
  const { data: turnaround = [], isLoading: loadingTurnaround } = useQuery({
    queryKey: ['reports', 'approval-turnaround'],
    queryFn: async () => (await apiClient.get<ApprovalTurnaroundRow[]>('/reports/approval-turnaround')).data,
    enabled: tab === 1,
  });

  const pendingColumns: DataTableColumn<ReturnType<typeof withId<PendingApprovalAgingRow>>[number]>[] = [
    { field: 'transactionType', headerName: t('columns.transactionType'), filterable: true },
    { field: 'currentLevelNo', headerName: t('columns.currentLevel') },
    { field: 'totalValue', headerName: t('columns.totalValue'), render: (r) => (r.totalValue != null ? `₹${r.totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—') },
    { field: 'createdDate', headerName: t('columns.raisedDate'), render: (r) => formatDate(r.createdDate) },
    { field: 'hoursPending', headerName: t('columns.hoursPending'), render: (r) => r.hoursPending.toFixed(1) },
    { field: 'slaHours', headerName: t('columns.slaHours'), render: (r) => r.slaHours ?? '—' },
    {
      field: 'isOverdue', headerName: t('columns.status'), filterable: true,
      render: (r) => <Chip size="small" label={r.isOverdue ? t('columns.overdue') : t('columns.withinSla')} color={r.isOverdue ? 'error' : 'success'} />,
    },
  ];

  const turnaroundColumns: DataTableColumn<ReturnType<typeof withId<ApprovalTurnaroundRow>>[number]>[] = [
    { field: 'transactionType', headerName: t('columns.transactionType'), filterable: true },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'createdDate', headerName: t('columns.raisedDate'), render: (r) => formatDate(r.createdDate) },
    { field: 'decidedDate', headerName: t('columns.decidedDate'), render: (r) => (r.decidedDate ? formatDate(r.decidedDate) : '—') },
    { field: 'decidedByUserName', headerName: t('columns.decidedBy'), render: (r) => r.decidedByUserName ?? '—' },
    { field: 'turnaroundHours', headerName: t('columns.turnaroundHours'), render: (r) => (r.turnaroundHours != null ? r.turnaroundHours.toFixed(1) : '—') },
  ];

  return (
    <>
      <PageHeader title={t('modules.approvals.title')} subtitle={t('modules.approvals.subtitle')} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((key) => (
          <Tab key={key} label={t(`${key}.title`)} />
        ))}
      </Tabs>

      <Box hidden={tab !== 0}>
        <DataTable columns={pendingColumns} rows={withId(pending, (r) => r.instanceId)} loading={loadingPending} searchable exportFileName="pending-approvals-aging" />
      </Box>
      <Box hidden={tab !== 1}>
        <DataTable columns={turnaroundColumns} rows={withId(turnaround, (r) => r.instanceId)} loading={loadingTurnaround} searchable exportFileName="approval-turnaround" />
      </Box>
    </>
  );
}
